import React from "react";
import {
  MessageSquare,
  Phone,
  Mail,
  Calendar,
  AlertCircle,
} from "lucide-react";
import { Button } from "./ui/button";
import { BookingLanguage } from "@/types/booking";
import { t } from "@/locales";

interface ErrorFallbackProps {
  language?: BookingLanguage;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  language = "en-US",
}) => {
  const translations = {
    title: t(language, "errorFallback.title"),
    subtitle: t(language, "errorFallback.subtitle"),
    contactTitle: t(language, "errorFallback.contactTitle"),
    whatsapp: t(language, "errorFallback.whatsapp"),
    callUs: t(language, "errorFallback.callUs"),
    email: t(language, "errorFallback.email"),
    bookCall: t(language, "errorFallback.bookCall"),
    bookCallSubtitle: t(language, "errorFallback.bookCallSubtitle"),
    phoneNumber: "+41 44 797 69 46",
    emailAddress: "get@aheadhealth.com",
    whatsappNumber: "+41 79 807 86 64",
    tryAgain: t(language, "errorFallback.tryAgain"),
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Main Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 md:p-12 space-y-8">
          {/* Header */}
          <div className="text-center space-y-4">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full">
              <AlertCircle className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-3xl md:text-4xl font-thin text-neutral-900">
              {translations.title}
            </h1>
            <p className="text-lg text-gray-600 max-w-md mx-auto">
              {translations.subtitle}
            </p>
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">
                {translations.contactTitle}
              </span>
            </div>
          </div>

          {/* Contact Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Book a Call - Primary CTA with purple gradient */}
            <Button
              onClick={() =>
                window.open(
                  "https://www.aheadhealth.com/free-call#book",
                  "_blank",
                )
              }
              className="h-auto py-4 px-6 flex flex-col items-start gap-2 bg-gradient-to-br from-[#4C2652] to-[#5C4279] hover:shadow-md text-white transition-all duration-200 group"
            >
              <div className="flex items-center gap-3 w-full">
                <Calendar className="h-5 w-5 group-hover:scale-110 transition-transform" />
                <span className="font-semibold uppercase text-xs tracking-wider">
                  {translations.bookCall}
                </span>
              </div>
              <span className="text-xs opacity-90 normal-case font-normal tracking-normal">
                {translations.bookCallSubtitle}
              </span>
            </Button>

            {/* WhatsApp */}
            <Button
              onClick={() => window.open("https://wa.me/41798078664", "_blank")}
              variant="outline"
              className="h-auto py-4 px-6 flex flex-col items-start gap-2 border-2 border-gray-200 hover:bg-blue-50 hover:border-blue-200 transition-all duration-200 group"
            >
              <div className="flex items-center gap-3 w-full">
                <MessageSquare className="h-5 w-5 text-blue-600 group-hover:scale-110 transition-transform" />
                <span className="font-semibold text-neutral-900 uppercase text-xs tracking-wider">
                  {translations.whatsapp}
                </span>
              </div>
              <span className="text-xs text-gray-600 normal-case font-normal tracking-normal">
                {translations.whatsappNumber}
              </span>
            </Button>

            {/* Phone */}
            <Button
              onClick={() => (window.location.href = "tel:+41447976946")}
              variant="outline"
              className="h-auto py-4 px-6 flex flex-col items-start gap-2 border-2 border-gray-200 hover:bg-blue-50 hover:border-blue-200 transition-all duration-200 group"
            >
              <div className="flex items-center gap-3 w-full">
                <Phone className="h-5 w-5 text-blue-600 group-hover:scale-110 transition-transform" />
                <span className="font-semibold text-neutral-900 uppercase text-xs tracking-wider">
                  {translations.callUs}
                </span>
              </div>
              <span className="text-xs text-gray-600 normal-case font-normal tracking-normal">
                {translations.phoneNumber}
              </span>
            </Button>

            {/* Email */}
            <Button
              onClick={() =>
                (window.location.href = "mailto:get@aheadhealth.com")
              }
              variant="outline"
              className="h-auto py-4 px-6 flex flex-col items-start gap-2 border-2 border-gray-200 hover:bg-blue-50 hover:border-blue-200 transition-all duration-200 group"
            >
              <div className="flex items-center gap-3 w-full">
                <Mail className="h-5 w-5 text-blue-600 group-hover:scale-110 transition-transform" />
                <span className="font-semibold text-neutral-900 uppercase text-xs tracking-wider">
                  {translations.email}
                </span>
              </div>
              <span className="text-xs text-gray-600 normal-case font-normal tracking-normal">
                {translations.emailAddress}
              </span>
            </Button>
          </div>

          {/* Try Again Button */}
          <div className="pt-4">
            <Button
              onClick={() => window.location.reload()}
              variant="ghost"
              className="w-full text-gray-600 hover:text-gray-900 hover:bg-gray-100 uppercase text-xs tracking-wider"
            >
              {translations.tryAgain}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorFallback;
